import type { ColorScheme } from '@myosh/design-system';
import { Children, type ComponentProps, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeStyling } from '../common-functions';
import AboutInfoModal, { type AboutInfoModalProps } from '../custom-modals/about-info-modal/about-info-modal.component';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../dropdown-menu/dropdown-menu';
import OdinIcon, { type OdinIconName, OdinIconSize } from '../odin-icon/odin-icon.component';
import { UserAvatar, type UserAvatarProps } from '../user-avatar/user-avatar.component';

/** Data passed to the built-in About dialog. Mirrors `AboutInfoModalProps` minus state props. */
type UserMenuAboutInfo = Omit<AboutInfoModalProps, 'isAboutInfoOpen' | 'onClose'>;

/** Scheme selectable from the built-in "Appearance" submenu. Type-only alias of the design-system `ColorScheme`. */
type UserMenuColorScheme = ColorScheme;

/** Schema metadata from the backend. Drives the About dialog (currentSchema/schemaTimeZone/region/cluster) and Log Out visibility (`hide_logout_button`). */
interface UserMenuSchemaInfo {
  schema?: string;
  'time-zone'?: string;
  region?: string;
  cluster?: string;
  hide_logout_button?: string;
}

/** Subset of the myosh-login user state used to derive `controllingSuperuser` and the current schema label. */
interface UserMenuAboutUser {
  superuser?: string;
  controlledUsername?: string;
  preferred_username?: string;
  currentSchema?: string;
}

interface UserMenuAboutConfig {
  /** App version string. */
  version?: string;
  /** Current schema label. Overrides the value derived from `schemaInfo.schema` and `user.currentSchema`. */
  currentSchema?: string;
  /** Additional dialog data (`openSourceNotices`, etc.). Overrides all derived/top-level values. */
  info?: UserMenuAboutInfo;
}

interface UserMenuProps {
  /** Current user state. Drives `controllingSuperuser`, the schema-label fallback, and the default `name`. */
  user?: UserMenuAboutUser;
  /** Backend schema info. Feeds the About dialog and hides Log Out when `hide_logout_button` is `'true'`. */
  schemaInfo?: UserMenuSchemaInfo;
  /** Standard "About" item + built-in dialog. Omit to hide. */
  about?: UserMenuAboutConfig;

  /** Handler for the "Select Schema" / "Select Company" item. Omit to hide. */
  onSelectSchema?: () => void;
  /** Handler for the "Select User" item (superuser controlled-user switching). Omit to hide. */
  onSelectUser?: () => void;
  /** Handler for the "Log Out" item. Omit to hide. Also hidden when `schemaInfo.hide_logout_button` is `'true'`. */
  onLogOut?: () => void;

  /** Selected scheme for the built-in "Appearance" submenu. Provide together with `onColorSchemeChange` to show it. */
  colorScheme?: UserMenuColorScheme;
  /** Change handler for the built-in "Appearance" submenu (e.g. the `useColorScheme` setter from `@myosh/design-system/react`). */
  onColorSchemeChange?: (colorScheme: UserMenuColorScheme) => void;

  /** App-specific items rendered between the built-in items and Log Out, separated from both. Compose with `UserMenuItem`, `UserMenuCheckboxItem`, `UserMenuRadioItem`, `UserMenuSeparator`. */
  children?: ReactNode;

  /** Full name used as the avatar trigger's alt text and accessible label. Defaults to `user.preferred_username`. */
  name?: string;
  /** Avatar image URL. Falls back to a generic user icon when absent or broken. */
  src?: string;
  /** Avatar size. Defaults to `default`. */
  avatarSize?: UserAvatarProps['size'];

  /** Class applied to the trigger button. */
  triggerClassName?: string;
  /** Class applied to the dropdown menu content. */
  contentClassName?: string;

  /** Menu content alignment along the trigger. Defaults to `end`. */
  align?: 'start' | 'center' | 'end';

  /** Controlled open state. */
  open?: boolean;
  /** Open-state change handler. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether the menu traps interaction while open (locks page scroll, disables outside pointer
   * events). Defaults to `true`. Set `false` when an external overlay, e.g. a feature tour
   * must stay interactive while the menu is open.
   */
  modal?: boolean;
}

const UserMenu = ({
  user,
  schemaInfo,
  about,
  onSelectSchema,
  onSelectUser,
  onLogOut,
  colorScheme,
  onColorSchemeChange,
  children,
  name,
  src,
  avatarSize = 'default',
  triggerClassName,
  contentClassName,
  align = 'end',
  open,
  onOpenChange,
  modal = true,
}: UserMenuProps) => {
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const { t } = useTranslation();
  const effectiveName = name ?? user?.preferred_username;

  const aboutVisible = about !== undefined;
  const selectSchemaVisible = onSelectSchema !== undefined;
  const selectControlledUserVisible = onSelectUser !== undefined;
  const colorSchemeVisible = colorScheme !== undefined && onColorSchemeChange !== undefined;
  const logOutVisible = onLogOut !== undefined && schemaInfo?.hide_logout_button !== 'true';

  const hasFeatureItems = aboutVisible || selectSchemaVisible || selectControlledUserVisible || colorSchemeVisible;
  const hasChildren = Children.toArray(children).length > 0;
  const hasHeader = Boolean(effectiveName);

  // Sections render top-to-bottom: header -> built-in items -> custom children → log out.
  // A separator precedes a section only when an earlier section is also present.
  const separatorBeforeFeatures = hasHeader && hasFeatureItems;
  const separatorBeforeChildren = (hasHeader || hasFeatureItems) && hasChildren;
  const separatorBeforeLogOut = (hasHeader || hasFeatureItems || hasChildren) && logOutVisible;

  const normalize = (value?: string) => value?.trim().toLowerCase();
  const username = user?.preferred_username;
  const secondaryName = username && normalize(username) !== normalize(effectiveName) ? username : undefined;

  // `preferred_username` is the authenticated JWT identity (the superuser themselves). The user
  // being controlled is tracked separately as `controlledUsername`. Surface both only while the
  // superuser is actively controlling someone.
  const controlledUsername = user?.superuser === 'true' ? user.controlledUsername : undefined;
  const controllingSuperuser = controlledUsername ? username : undefined;
  // Schema label precedence: explicit `about.currentSchema` > backend `schemaInfo.schema` > JWT `user.currentSchema`.
  const currentSchemaText = about?.currentSchema ?? schemaInfo?.schema ?? user?.currentSchema ?? 'n/a';

  return (
    <>
      <DropdownMenu open={open} onOpenChange={onOpenChange} modal={modal}>
        <DropdownMenuTrigger
          aria-label={effectiveName ?? t('open-user-menu')}
          className={mergeStyling(
            'rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            triggerClassName
          )}
        >
          <UserAvatar name={effectiveName ?? ''} src={src} size={avatarSize} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className={mergeStyling('w-auto min-w-[200px]', contentClassName)}>
          <DropdownMenuGroup>
            {effectiveName && (
              <DropdownMenuLabel className="flex flex-col gap-0.5">
                <span className="truncate font-medium text-foreground text-sm" title={effectiveName}>
                  {effectiveName}
                </span>
                {secondaryName && (
                  <span className="truncate font-normal text-muted-foreground text-xs" title={secondaryName}>
                    {secondaryName}
                  </span>
                )}
                {controlledUsername && (
                  <span className="truncate font-normal text-muted-foreground text-xs" title={controlledUsername}>
                    {t('controlling-user', { user: controlledUsername })}
                  </span>
                )}
              </DropdownMenuLabel>
            )}
            {separatorBeforeFeatures && <DropdownMenuSeparator />}
            {aboutVisible && (
              <UserMenuItem
                icon="Info"
                label={t('about')}
                onClick={() => setIsAboutOpen(true)}
                className="about-menu-item"
              />
            )}
            {selectSchemaVisible && (
              <UserMenuItem
                icon="ListIndentDecrease"
                label={user?.superuser === 'true' ? t('select-schema') : t('select-company')}
                onClick={onSelectSchema}
              />
            )}
            {selectControlledUserVisible && (
              <UserMenuItem icon="UserCog" label={t('select-user')} onClick={onSelectUser} />
            )}
            {colorSchemeVisible && (
              <UserMenuSub>
                <UserMenuSubTrigger icon="Contrast" label={t('appearance')} className="appearance-menu-item" />
                <UserMenuSubContent>
                  <UserMenuRadioGroup
                    value={colorScheme}
                    onValueChange={(value) => {
                      if (value === 'system' || value === 'light' || value === 'dark') {
                        onColorSchemeChange?.(value);
                      }
                    }}
                  >
                    <UserMenuRadioItem icon="Sun" label={t('light')} value="light" />
                    <UserMenuRadioItem icon="Moon" label={t('dark')} value="dark" />
                    <UserMenuRadioItem icon="Monitor" label={t('system')} value="system" />
                  </UserMenuRadioGroup>
                </UserMenuSubContent>
              </UserMenuSub>
            )}
            {separatorBeforeChildren && <DropdownMenuSeparator />}
            {children}
            {separatorBeforeLogOut && <DropdownMenuSeparator />}
            {logOutVisible && (
              <UserMenuItem
                icon="LogOut"
                label={t('log-out')}
                onClick={onLogOut}
                variant="destructive"
                className="logout-menu-item"
              />
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {about && (
        <AboutInfoModal
          isAboutInfoOpen={isAboutOpen}
          onClose={() => setIsAboutOpen(false)}
          showUserTimeZone
          version={about.version}
          region={schemaInfo?.region}
          cluster={schemaInfo?.cluster}
          schemaTimeZone={schemaInfo?.['time-zone']}
          currentSchema={{ text: currentSchemaText }}
          controllingSuperuser={controllingSuperuser}
          {...about.info}
        />
      )}
    </>
  );
};

interface UserMenuItemProps extends Omit<ComponentProps<typeof DropdownMenuItem>, 'children'> {
  icon: OdinIconName;
  label: string;
}

const UserMenuItem = ({ icon, label, ...props }: UserMenuItemProps) => (
  <DropdownMenuItem {...props}>
    <OdinIcon icon={icon} size={OdinIconSize.Small} />
    <span>{label}</span>
  </DropdownMenuItem>
);

interface UserMenuCheckboxItemProps extends Omit<ComponentProps<typeof DropdownMenuCheckboxItem>, 'children'> {
  icon: OdinIconName;
  label: string;
}

const UserMenuCheckboxItem = ({ icon, label, ...props }: UserMenuCheckboxItemProps) => (
  <DropdownMenuCheckboxItem {...props}>
    <OdinIcon icon={icon} size={OdinIconSize.Small} />
    <span>{label}</span>
  </DropdownMenuCheckboxItem>
);

interface UserMenuRadioItemProps extends Omit<ComponentProps<typeof DropdownMenuRadioItem>, 'children'> {
  icon: OdinIconName;
  label: string;
}

const UserMenuRadioItem = ({ icon, label, ...props }: UserMenuRadioItemProps) => (
  <DropdownMenuRadioItem {...props}>
    <OdinIcon icon={icon} size={OdinIconSize.Small} />
    <span>{label}</span>
  </DropdownMenuRadioItem>
);

interface UserMenuSubTriggerProps extends Omit<ComponentProps<typeof DropdownMenuSubTrigger>, 'children'> {
  icon: OdinIconName;
  label: string;
}

const UserMenuSubTrigger = ({ icon, label, ...props }: UserMenuSubTriggerProps) => (
  <DropdownMenuSubTrigger {...props}>
    <OdinIcon icon={icon} size={OdinIconSize.Small} />
    <span>{label}</span>
  </DropdownMenuSubTrigger>
);

const UserMenuLabel = DropdownMenuLabel;
const UserMenuRadioGroup = DropdownMenuRadioGroup;
const UserMenuSeparator = DropdownMenuSeparator;
const UserMenuSub = DropdownMenuSub;
const UserMenuSubContent = DropdownMenuSubContent;

export type {
  UserMenuAboutConfig,
  UserMenuAboutInfo,
  UserMenuAboutUser,
  UserMenuCheckboxItemProps,
  UserMenuColorScheme,
  UserMenuItemProps,
  UserMenuProps,
  UserMenuRadioItemProps,
  UserMenuSchemaInfo,
  UserMenuSubTriggerProps,
};
export {
  UserMenu,
  UserMenuCheckboxItem,
  UserMenuItem,
  UserMenuLabel,
  UserMenuRadioGroup,
  UserMenuRadioItem,
  UserMenuSeparator,
  UserMenuSub,
  UserMenuSubContent,
  UserMenuSubTrigger,
};
